export function getUserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function listTimeZones(): string[] {
  const supported = (
    Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  let zones: string[] = [];
  if (typeof supported === 'function') {
    try {
      zones = supported.call(Intl, 'timeZone');
    } catch {
      zones = [];
    }
  }
  if (zones.length === 0) {
    zones = [getUserTimeZone()];
  }
  const withUtc = zones.includes('UTC') ? zones : ['UTC', ...zones];
  return withUtc;
}

export function precisionIncludesTime(precision: string): boolean {
  return precision === 'hour' || precision === 'minute' || precision === 'second';
}

export function resolveTimelineTimeZone(stored: string | null | undefined): string {
  return stored?.trim() || 'UTC';
}

function readZonedParts(
  date: Date,
  timeZone: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value;
    return value != null ? Number(value) : 0;
  };
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = readZonedParts(date, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  return asUtc - date.getTime();
}

export function zonedWallTimeToUtcDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset1 = getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
  const date1 = new Date(utcGuess - offset1);
  const offset2 = getTimeZoneOffsetMs(date1, timeZone);
  return new Date(utcGuess - offset2);
}

export function utcIsoToZonedParts(
  iso: string,
  timeZone: string
): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  return readZonedParts(new Date(iso), timeZone);
}

export function formatTimeZoneLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const tryName = (timeZoneName: 'shortOffset' | 'short'): string | undefined => {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName,
        hour: 'numeric',
      }).formatToParts(at);
      return parts.find((part) => part.type === 'timeZoneName')?.value;
    };
    const offset = tryName('shortOffset') ?? tryName('short');
    return offset ? `${timeZone} (${offset})` : timeZone;
  } catch {
    return timeZone;
  }
}
